import React, { useState, useContext, useRef, useCallback, useEffect } from 'react';
import { Popover, Spin } from 'antd';
import { FilterContextData } from '../../contexts/FilterContext';
import FIlterSideBarUIElement from 'components/atoms/Filter/FIlterSideBarUIElement';
import FilterExpand from 'components/molecules/Filter/FilterExpand';
import FilterTimePeriod from 'components/molecules/Filter/FilterTimePeriod';
import { useDownloadAllAttributeNames } from 'hooks/Filter/useDownloadAllAttributeNames';
import FilterApplyButton from 'components/molecules/Filter/FilterApplyButton';
import FilterAttributeTypeList from 'components/molecules/Filter/FilterAttributeTypeList';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, sendNotification } from 'utils/helperMethods';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import _, { without } from 'lodash';
import { trackEventMixpanel } from 'utils/mixpanel';
import { GLOBAL_FILTER_EXPAND_COLLAPSE, GLOBAL_FILTER_RESET_CLICKED } from 'utils/mixpanelEventNames';
import { isPpaPerfSummaryScreen } from 'config/urls';
import { useOnClickOutside } from 'hooks/general/useOnOutsideClick';
import CampaignFilterTimePeriod from 'components/molecules/Filter/CampaignFilterTimePeriod';
import LoadPreset from 'components/molecules/Filter/LoadPreset';
import SaveSelectionPresetsModal from 'components/molecules/Filter/SaveSelectionPresetsModal';
import LoadPresetsModal from 'components/molecules/Filter/LoadPresetsModal';
import useSavePreset from 'hooks/Filter/useSavePreset';
import useFetchAllPresets from 'hooks/Filter/useFetchLoadAllPresets';
import Minus from 'assets/svg/Minus';
import Plus from 'assets/svg/Plus';
import DownSvg from 'assets/svg/Down';
import useTierList from 'hooks/pricingPool/useTierList';
import TierFilter from './TierFilter';

interface Props {
    /** Left edge of the fixed shell. Navbar V2 passes the rail width; V1 leaves it flush-left. */
    leftOffset?: string;
    /** Top edge of the fixed shell. Unset keeps V1's navbar-height margin-top utility class. */
    topOffset?: string;
    /** Navbar V2's MainContainer owns `#main-container` margin-left, so it opts out here. */
    ownsMainContainerMargin?: boolean;
}

const getShellPlacement = (leftOffset?: string, topOffset?: string) => {
    if (!leftOffset && !topOffset) return { className: 'main-screen-margin-top-no-margin-top', style: {} };
    return { className: '', style: { top: topOffset, left: leftOffset } };
};

export default function Filter({ leftOffset, topOffset, ownsMainContainerMargin = true }: Props) {
    const {
        allDimAndAttributeType,
        setIdForRestOfComponent,
        setAllCheckedAttribute,
        allCheckedAttribute,
        setFilterBLoading,
        timePeriodSelection,
        setTimePeriodSelection,
        applyFilterButtonObject,
        applyFilterObject,
        setIdForRestOfComponentLoading,
        setApplyFilterObject,
        setPreviousSelectedAttributesInUI,
        setRecentIdFromAttributeSelection,
        allSelectedValueForAllAttribute,
        setIdForRestOfComponentFilterB,
        loadingForAllCheckAttribute,
        timePeriodList,
        setFilterNullError,
        setTimePeriodList,
        setTimeSelectionLabel,
        timeSelectionLabel,
        latestDate,
        setVisibleDatePicker,
        expandFilterBol,
        setExpandFilterBol,
        fetchSinglePresetWrapper,
        isGlobalFilterDisabled,
        screenFilterConfig,
        hideGlobalTimeFilterComponent,
        disablePresetFilter,
        showTierFilter,
        setMultiSelectedTiers,
        setSingleSelectedTier,
        setTempMultiSelectedTiers,
        setTempSingleSelectedTier,
        isGlobalFilterCollapsed,
        setPpaGlobalFilterApplied,
        screenName,
    } = useContext(FilterContextData);
    const [attributeTypeExpandClose, setAttributeTypeExpandClose] = useState('');

    //PRESET
    const [isLoadPresetsOpen, setIsLoadPresetsOpen] = useState(false);
    const [isPresetsSaveSelectionOpen, setIsPresetsSaveSelectionOpen] = useState(false);

    const { appConfig } = useContext(UserContext);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const defaultTierFilterSelections = profitPoolConfig?.defaultTierFilterSelections || {
        multiSelect: [],
        singleSelect: [],
    };
    const outsideClickRef = useRef<any>();
    const { applyFilterMutate: applyFilterForFilterB } = useApplyFilterMutation({
        onComplete: onApplyFilterCompleteFilterB,
    });
    const globalFilterContainerId = 'parentGlobalFilterContainer';

    const { data: tierListData, loading, error } = useTierList(showTierFilter);

    const [isTierFilterExpanded, setIsTierFilterExpanded] = useState(false);
    const [showTierSelectionDropdown, setShowTierSelectionDropdown] = useState(false);
    const {
        loading: savePresetLoading,
        error: savePresetError,
        savePreset,
    } = useSavePreset({ onComplete: onSaveComplete });
    const {
        loading: fetchAllPresetsLoading,
        error: fetchAllPresetsError,
        fetchAllPresets,
        data: fetchAllPresetsData,
    } = useFetchAllPresets();

    function onApplyFilterCompleteFilterB(payload: any) {
        setIdForRestOfComponentFilterB(payload);
    }
    const expandFilter = () => {
        setExpandFilterBol(!expandFilterBol);

        trackEventMixpanel(GLOBAL_FILTER_EXPAND_COLLAPSE, {
            isExpanded: expandFilterBol,
        });
    };

    // When collapsed, main content uses CSS margin to sit beside the slim sidebar (matches --global-filter-expand-width)
    useEffect(() => {
        if (!ownsMainContainerMargin) {
            return;
        }

        const mainContainer = document.getElementById('main-container');
        if (!mainContainer) {
            return;
        }

        if (isGlobalFilterCollapsed) {
            mainContainer.style.marginLeft = 'var(--global-filter-expand-width)';
        } else {
            mainContainer.style.removeProperty('margin-left');
        }

        return () => {
            mainContainer.style.removeProperty('margin-left');
        };
    }, [isGlobalFilterCollapsed, ownsMainContainerMargin]);

    const { functionTOCallAllAttributeName, loading: allAttributeLoading } = useDownloadAllAttributeNames({
        setAllCheckedAttribute,
    });

    const { applyFilterMutate, loading: applyFilterLoad } = useApplyFilterMutation({
        onComplete: onApplyFilterComplete,
    });

    function onApplyFilterComplete(payload: any) {
        setIdForRestOfComponent(payload);
        if (isPpaPerfSummaryScreen(screenName)) {
            setPpaGlobalFilterApplied(true);
        }
        setRecentIdFromAttributeSelection(() => ({
            attr: payload,
        }));
    }

    function clearAll() {
        trackEventMixpanel(GLOBAL_FILTER_RESET_CLICKED);
        if (isPpaPerfSummaryScreen(screenName)) {
            setPpaGlobalFilterApplied(false);
        }
        setIdForRestOfComponent({
            dim1: { ids: [] },
            dim2: { ids: [] },
        });

        setPreviousSelectedAttributesInUI({
            dim1: ['attr', 'attr'],
            dim2: ['attr', 'attr'],
        });
        setRecentIdFromAttributeSelection({
            attr: {
                dim1: { ids: [] },
                dim2: { ids: [] },
            },
        });
        setAttributeTypeExpandClose('');
        setExpandFilterBol(true);
        fetchSinglePresetWrapper({}, { isReset: true });
        //set default selection
        setVisibleDatePicker(false);
        // update filter null error
        setFilterNullError(undefined);

        setTempMultiSelectedTiers(defaultTierFilterSelections.multiSelect);
        setTempSingleSelectedTier(defaultTierFilterSelections.singleSelect);
        setMultiSelectedTiers(defaultTierFilterSelections.multiSelect);
        setSingleSelectedTier(defaultTierFilterSelections.singleSelect);
    }

    //close expanded filter on clicking outside
    const clickOutsideHandler = useCallback(
        (event: any) => {
            if (!expandFilterBol) {
                expandFilter();
            }
        },
        [expandFilterBol],
    );
    useOnClickOutside(outsideClickRef, clickOutsideHandler);

    function onLoadClickPreset(item: any) {
        fetchSinglePresetWrapper({
            presetFilterName: item.value,
            defaultFlag: item.isDefault,
        });
    }

    function onSaveComplete(payload: any) {
        setIsPresetsSaveSelectionOpen(false);
    }

    //called when clicked on save button inside preset modal
    function onSaveClick(item: any) {
        const userEmail = localStorage.getItem('email');
        if (!userEmail)
            return sendNotification('error', 'Please login to the application again to perform this action.');
        const variables = {
            presetFilterJson: JSON.stringify({
                filterObj: {
                    applyFilterObject,
                    allCheckedAttribute,
                    allSelectedValueForAllAttribute,
                },
                timePeriodFilterObj: {
                    timePeriodSelection,
                    timeSelectionLabel,
                },
            }),
            presetFilterName: item.label,
            userEmail,
            defaultFlag: item.isDefault,
        };
        savePreset({
            variables,
        });
    }

    const getCustomCalender = () => {
        if (commonConfig.showCampignCalender) {
            return (
                // CAMPAIGN calender
                <CampaignFilterTimePeriod
                    latestDate={latestDate}
                    timePeriod={timePeriodList}
                    attributeTypeExpandClose={attributeTypeExpandClose}
                    setAttributeTypeExpandClose={setAttributeTypeExpandClose}
                    timePeriodSelection={timePeriodSelection}
                    setTimePeriodSelection={setTimePeriodSelection}
                    setTimePeriodList={setTimePeriodList}
                    setTimeSelectionLabel={setTimeSelectionLabel}
                    timeSelectionLabel={timeSelectionLabel}
                />
            );
        }

        return (
            <FilterTimePeriod
                latestDate={latestDate}
                timePeriod={timePeriodList}
                attributeTypeExpandClose={attributeTypeExpandClose}
                setAttributeTypeExpandClose={setAttributeTypeExpandClose}
                timePeriodSelection={timePeriodSelection}
                setTimePeriodSelection={setTimePeriodSelection}
                setTimePeriodList={setTimePeriodList}
                setTimeSelectionLabel={setTimeSelectionLabel}
                timeSelectionLabel={timeSelectionLabel}
            />
        );
    };

    const placement = getShellPlacement(leftOffset, topOffset);

    return (
        // Outer shell stays fixed; margin-top class pairs with top ribbon when collapsible (no duplicate top gap)
        <div
            ref={outsideClickRef}
            className={`flex z-[51] fixed top-0 bottom-16 ${placement.className} ${
                isGlobalFilterDisabled ? 'cursor-not-allowed' : ''
            }`}
            style={placement.style}
        >
            <div
                className={`bg-white global-filter-width pt-4 relative z-20 border-r border-borderLine ${
                    isGlobalFilterDisabled ? 'pointer-events-none' : ''
                }`}
                style={
                    // Animate sidebar to zero width when collapsed; expand/collapse control stays in ribbon
                    isGlobalFilterCollapsed
                        ? {
                              width: 0,
                              minWidth: 0,
                              overflow: 'hidden',
                              paddingTop: 0,
                              transition: 'width 500ms ease',
                          }
                        : { transition: 'width 500ms ease' }
                }
            >
                <div className="flex justify-between px-3.2 items-center">
                    <div className="font-md font-medium">Global Filter</div>
                    <span className="font-xs text-purpleFont font-medium cursor-pointer" onClick={() => clearAll()}>
                        Reset
                    </span>
                </div>
                {loadingForAllCheckAttribute ? (
                    <div className="flex justify-center items-center h-[78%]">
                        <Spin />
                    </div>
                ) : (
                    <div className="overflow-y-auto h-[78%]" id={globalFilterContainerId}>
                        {showTierFilter && <TierFilter />}
                        {!hideGlobalTimeFilterComponent && getCustomCalender()}
                        <FilterAttributeTypeList
                            globalFilterContainerId={globalFilterContainerId}
                            allDimAndAttributeType={allDimAndAttributeType}
                            allCheckedAttribute={allCheckedAttribute}
                            attributeTypeExpandClose={attributeTypeExpandClose}
                            setAttributeTypeExpandClose={setAttributeTypeExpandClose}
                            expandFilter={expandFilter}
                            screenFilterConfig={screenFilterConfig}
                            uppercaseFilterLabels={globalFilterConfig?.uppercaseFilterLabels}
                        />
                    </div>
                )}
                {!disablePresetFilter && (
                    <LoadPreset
                        onClick={() => {
                            const userEmail = localStorage.getItem('email');
                            if (!userEmail)
                                return sendNotification(
                                    'error',
                                    'Please login to the application again to perform this action.',
                                );
                            //load the list here
                            fetchAllPresets({
                                variables: { userId: userEmail },
                            });
                            setIsLoadPresetsOpen(true);
                        }}
                    />
                )}

                <FilterApplyButton
                    setIdForRestOfComponent={setIdForRestOfComponent}
                    applyFilterObject={applyFilterObject}
                    applyFilterButtonObject={applyFilterButtonObject}
                    setIdForRestOfComponentLoading={setIdForRestOfComponentLoading}
                    setIdForRestOfComponentFilterB={setIdForRestOfComponentFilterB}
                    setFilterNullError={setFilterNullError}
                    allCheckedAttribute={allCheckedAttribute}
                    setFilterBLoading={setFilterBLoading}
                    setAttributeTypeExpandClose={setAttributeTypeExpandClose}
                />
                <SaveSelectionPresetsModal
                    loading={savePresetLoading}
                    onSaveClick={onSaveClick}
                    isOpen={isPresetsSaveSelectionOpen}
                    setIsOpen={setIsPresetsSaveSelectionOpen}
                />
                <LoadPresetsModal
                    loading={fetchAllPresetsLoading}
                    onLoad={onLoadClickPreset}
                    presetsList={fetchAllPresetsData}
                    isOpen={isLoadPresetsOpen}
                    setIsOpen={setIsLoadPresetsOpen}
                />
            </div>
            {/* Hide expand / preset panel column while sidebar is collapsed (ribbon drives interaction) */}
            {isGlobalFilterCollapsed ? null : expandFilterBol ? (
                <FIlterSideBarUIElement expandFilter={expandFilter} isGlobalFilterDisabled={isGlobalFilterDisabled} />
            ) : (
                <FilterExpand
                    onSaveSelection={() => setIsPresetsSaveSelectionOpen(true)}
                    expandFilter={expandFilter}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    setApplyFilterObject={setApplyFilterObject}
                    applyFilterObject={applyFilterObject}
                    allAttributeCallLoading={loadingForAllCheckAttribute}
                    setAttributeTypeExpandClose={setAttributeTypeExpandClose}
                    timeSelectionLabel={timeSelectionLabel}
                    screenFilterConfig={screenFilterConfig}
                />
            )}
        </div>
    );
}
